import type {
  ActiveFileId,
  ProcessedBusinessDocument,
} from '@/components/InvoiceReportDetailDrawer/ApplicationContent';
import {
  BusinessDocumentsFieldView,
  CombineLabelFieldView,
  CombineLabelFieldViewWithLink,
  DefaultFieldView,
  FileFieldView,
  InvoiceFileFieldView,
  InvoiceRegistrationNumberFieldView,
} from '@/components/InvoiceReportDetailDrawer/ApplicationContent';
import { useBusinessDocumentsContext } from '@/components/InvoiceReportDetailDrawer/ApplicationContent/context/BusinessDocumentsContext';
import {
  ListViewBody,
  ListViewLabel,
  Single,
} from '@moneyforward/ap-frontend-components';
import {
  InvoiceReportFormInputResponse,
  InvoiceReportPaymentInfoResponse,
  reportFormInputType,
} from 'ap-openapi';
import classNames from 'classnames/bind';
import { FC, memo } from 'react';
import styles from '../styles.module.scss';

const cx = classNames.bind(styles);

type BusinessDocumentsProps = {
  processedDocuments: ProcessedBusinessDocument[];
  selectDocumentFile: (document: ProcessedBusinessDocument) => () => void;
  activeFile: ActiveFileId;
};

const switchViewType = (
  type: string,
  item: InvoiceReportFormInputResponse,
  businessDocumentsProps: BusinessDocumentsProps
) => {
  switch (type) {
    case reportFormInputType.ApReportFormInputFileField:
      return <FileFieldView item={item} />;
    case reportFormInputType.ApReportFormInputBusinessDocumentsField:
      return <BusinessDocumentsFieldView {...businessDocumentsProps} />;
    case reportFormInputType.ApReportFormInputInvoiceFileField:
      return <InvoiceFileFieldView item={item} />;
    case reportFormInputType.ApReportFormInputCheckBox:
      return <CombineLabelFieldView item={item} />;
    case reportFormInputType.ApReportFormInputReportNumberForExpenseField:
    case reportFormInputType.ApReportFormInputContractNumberField:
      return <CombineLabelFieldViewWithLink item={item} />;
    case reportFormInputType.ApReportFormInputInvoiceRegistrationNumberField:
      return <InvoiceRegistrationNumberFieldView item={item} />;
    default:
      return <DefaultFieldView item={item} />;
  }
};

type SmartListViewDetailsProps = {
  data: InvoiceReportPaymentInfoResponse;
};

export const SmartListViewDetails: FC<SmartListViewDetailsProps> = memo(
  ({ data }) => {
    const { isVisible, processedDocuments, selectDocumentFile, activeFile } =
      useBusinessDocumentsContext();

    const businessDocumentsProps: BusinessDocumentsProps = {
      processedDocuments,
      selectDocumentFile,
      activeFile,
    };

    return (
      <ListViewBody className={cx('applicationContentContainer')}>
        {data?.report_form_inputs?.map((item, index) => {
          if (!isVisible(item)) {
            return null;
          }

          return (
            <div
              key={item?.label ?? `empty-${index}`}
              className={cx('singleContainer')}
            >
              <Single>
                <ListViewLabel key='left'>{item?.label ?? ''}</ListViewLabel>
                <ListViewBody
                  className={cx('listViewBodyContainer')}
                  key='right'
                >
                  {switchViewType(
                    item?.type ?? '',
                    item,
                    businessDocumentsProps
                  )}
                </ListViewBody>
              </Single>
            </div>
          );
        })}
      </ListViewBody>
    );
  }
);
SmartListViewDetails.displayName = 'SmartListViewDetails';
